// Shared CRS picker: one searchable control for every place the app asks for a coordinate system.
//
// Replaces the plain <select> lists (which only ever held the ~250 BUILTIN_CRS) with search over the
// full EPSG catalogue (~6,000 systems, see projections.js). Opens with what fits the active project
// on top (systems whose area of use contains it -- State Plane first), then recently used ones; type a
// code or words ("6339", "2011 utm 17", "texas south ft"). Units and datum are shown on every row so a
// feet/metre or NAD83/NAD83(2011) mix-up is visible before it's made.
//
// Drop-in for template-string UIs: crsPickerField() renders a hidden <input id=...> that keeps the old
// select's id and .value contract (listeners bound to "change" on it keep working), and
// enhanceCrsPickers(container, app) mounts the control after each render.

const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

/**
 * @param {string} id      element id the tool already reads (`#id`.value) -- kept on a hidden input
 * @param {string} value   current code ("EPSG:2236") or a special value from `extra`
 * @param {object} [opts]
 * @param {{value:string,label:string,hint?:string}[]} [opts.extra]  special choices shown first (e.g. "auto")
 * @param {string} [opts.placeholder]
 * @param {string} [opts.style]  inline style for the field
 * @param {[number,number]} [opts.point]  [lon,lat] for "fits" suggestions; default = the active project
 */
export function crsPickerField(id, value, { extra = [], placeholder = "Choose CRS…", style = "", point = null } = {}) {
  const v = String(value ?? "");
  const pt = Array.isArray(point) && point.every((n) => Number.isFinite(Number(n))) ? `${Number(point[0])},${Number(point[1])}` : "";
  return `<span class="crs-picker" data-crs-picker data-point="${esc(pt)}" data-extra="${esc(JSON.stringify(extra))}" data-placeholder="${esc(placeholder)}" style="display:inline-flex; max-width:100%; min-width:0; ${esc(style)}">`
    + `<input type="hidden" id="${esc(id)}" value="${esc(v)}" data-initial="${esc(v)}" />`
    + `<button type="button" class="btn crs-picker-btn" style="flex:1; min-width:0; justify-content:flex-start; text-align:left; padding:3px 8px; font-size:11px; overflow:hidden; white-space:nowrap; text-overflow:ellipsis;" title="${esc(v)}">${esc(v || placeholder)}</button>`
    + `</span>`;
}

// [lon,lat] for suggestions: the active project, else the middle of the map
function projectPoint(app) {
  const p = app?.services?.context?.getActiveProject?.();
  const lat = Number(p?.location?.lat ?? p?.lat), lng = Number(p?.location?.lng ?? p?.lng);
  if (Number.isFinite(lat) && Number.isFinite(lng)) return [lng, lat];
  try { const c = app?.map?.instance?.getCenter?.(); if (c && Number.isFinite(c.lat)) return [c.lng, c.lat]; } catch {}
  return null;
}
const pointSource = (app) => (app?.services?.context?.getActiveProject?.() ? "Fits the active project" : "Fits the map view");
const badge = (t, color = "rgba(148,163,184,.35)") => `<span style="font-size:9.5px; border:1px solid ${color}; border-radius:6px; padding:0 4px; margin-left:4px; white-space:nowrap;">${esc(t)}</span>`;
function rowHtml(e, active) {
  const fam = (e.name.match(/^[^/]+/) || [""])[0].trim();
  const unitColor = e.units === "ftUS" ? "#38bdf8" : e.units === "ft" ? "#f59e0b" : e.units === "m" ? "#22c55e" : "rgba(148,163,184,.5)";
  return `<div class="crs-picker-row" data-code="${esc(e.code)}" role="option" aria-selected="${active}" style="padding:5px 8px; cursor:pointer; border-radius:6px; ${active ? "background:rgba(56,189,248,.18);" : ""} ${e.proj4 === "" && e.fromCatalog ? "opacity:.5;" : ""}">
    <div style="font-size:12px; color:#e2e8f0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${esc(e.name)}</div>
    <div style="font-size:10.5px; color:var(--muted,#94a3b8); display:flex; align-items:center; flex-wrap:wrap;">${esc(e.code)}${e.unitsLabel ? badge(e.unitsLabel, unitColor) : ""}${fam && fam !== e.name ? badge(fam) : ""}${e.kind === "geographic" ? badge("lat/lon") : ""}${e.fitsPoint ? badge("fits project", "#22c55e") : ""}${e.proj4 === "" && e.fromCatalog ? badge("no in-browser transform", "#f87171") : ""}</div>
  </div>`;
}

let openPopover = null;
function closePopover() { if (openPopover) { openPopover.cleanup(); openPopover = null; } }

function openPicker(field, app) {
  closePopover();
  const svc = app.services.projections;
  const hidden = field.querySelector("input[type=hidden]");
  const btn = field.querySelector(".crs-picker-btn");
  const extra = JSON.parse(field.dataset.extra || "[]");
  const point = field.dataset.point ? field.dataset.point.split(",").map(Number) : projectPoint(app);
  const pop = document.createElement("div");
  pop.className = "card crs-picker-pop";
  const r = btn.getBoundingClientRect();
  const width = Math.max(320, Math.min(440, window.innerWidth - 16));
  const left = Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
  const below = window.innerHeight - r.bottom > 320;
  pop.style.cssText = `position:fixed; z-index:100000; left:${left}px; ${below ? `top:${r.bottom + 4}px;` : `bottom:${window.innerHeight - r.top + 4}px;`} width:${width}px; max-height:min(440px, 70vh); display:flex; flex-direction:column; padding:8px; box-shadow:0 12px 32px rgba(0,0,0,.45);`;
  pop.innerHTML = `<input type="search" class="crs-picker-q" placeholder="Search EPSG code or name — e.g. 2236, 2011 utm 17, texas south ft" style="width:100%; box-sizing:border-box; padding:6px 8px; font-size:12px; margin-bottom:6px;" />
    <div class="crs-picker-list" role="listbox" style="overflow:auto; flex:1; min-height:60px;"></div>
    <div class="crs-picker-foot" style="font-size:10px; color:var(--muted,#94a3b8); margin-top:4px;"></div>`;
  document.body.appendChild(pop);
  const q = pop.querySelector(".crs-picker-q"), list = pop.querySelector(".crs-picker-list"), foot = pop.querySelector(".crs-picker-foot");
  let items = [], active = 0;
  const section = (t) => `<div style="font-size:9.5px; text-transform:uppercase; letter-spacing:.06em; color:var(--muted,#94a3b8); margin:6px 4px 2px;">${esc(t)}</div>`;
  function draw() {
    const text = q.value.trim();
    let html = ""; items = [];
    const add = (title, rows) => {
      const fresh = rows.filter((e) => e && !items.some((x) => x.code === e.code));
      if (!fresh.length) return;
      html += section(title);
      for (const e of fresh) { html += rowHtml(e, items.length === active); items.push(e); }
    };
    if (!text) {
      add("Choices", extra.map((x) => ({ code: x.value, name: x.label, unitsLabel: x.hint || "", special: true })));
      const cur = hidden.value && !extra.some((x) => x.value === hidden.value) ? svc.getCRS(hidden.value) : null;
      add("Current", cur ? [cur] : []);
      add(field.dataset.point ? "Fits this project" : pointSource(app), point ? svc.suggestCRS(point, { limit: 8 }) : []);
      add("Recently used", svc.recentCRS().map((c) => svc.getCRS(c)).filter(Boolean));
      if (!svc.isCatalogLoaded()) html += `<div style="padding:8px; font-size:11px; color:var(--muted,#94a3b8);">Loading the EPSG catalogue…</div>`;
    } else {
      const res = svc.searchCRS(text, { point, limit: 80 });
      add(`${res.length >= 80 ? "80+" : res.length} matches`, res);
      if (!res.length) html += `<div style="padding:8px; font-size:11px; color:var(--muted,#94a3b8);">${svc.isCatalogLoaded() ? "No CRS matches — try fewer words or the EPSG number." : "Loading the EPSG catalogue…"}</div>`;
    }
    list.innerHTML = html;
    foot.textContent = svc.isCatalogLoaded() ? "↑↓ to move · Enter to choose · Esc to close" : "";
    list.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
  }
  function choose(e) {
    if (!e) return;
    if (e.fromCatalog && !e.proj4) return;   // listed for reference only
    hidden.value = e.code;
    btn.textContent = e.special ? e.name : `${e.name} · ${e.code}`;
    btn.title = `${e.code}${e.unitsLabel ? ` · ${e.unitsLabel}` : ""}${e.datum ? ` · ${e.datum.replace(/_/g, " ")}` : ""}`;
    if (!e.special) { svc.ensureCRS(e.code).catch(() => {}); svc.noteRecentCRS(e.code); }
    closePopover();
    hidden.dispatchEvent(new Event("change", { bubbles: true }));
    hidden.dispatchEvent(new Event("input", { bubbles: true }));
    btn.focus();
  }
  q.addEventListener("input", () => { active = 0; draw(); });
  q.addEventListener("keydown", (ev) => {
    if (ev.key === "ArrowDown") { active = Math.min(items.length - 1, active + 1); draw(); ev.preventDefault(); }
    else if (ev.key === "ArrowUp") { active = Math.max(0, active - 1); draw(); ev.preventDefault(); }
    else if (ev.key === "Enter") { choose(items[active]); ev.preventDefault(); }
    else if (ev.key === "Escape") { closePopover(); btn.focus(); ev.preventDefault(); }
    ev.stopPropagation();   // keep tool hotkeys out of the search box
  });
  list.addEventListener("mousedown", (ev) => ev.preventDefault());
  list.addEventListener("click", (ev) => { const row = ev.target.closest(".crs-picker-row"); if (row) choose(items.find((x) => x.code === row.dataset.code)); });
  const outside = (ev) => { if (!pop.contains(ev.target) && !field.contains(ev.target)) closePopover(); };
  setTimeout(() => document.addEventListener("mousedown", outside, true), 0);
  const onCatalog = () => draw();
  svc.loadCatalog().then(onCatalog).catch(() => { foot.textContent = "EPSG catalogue unavailable — only built-in systems can be found."; });
  openPopover = { cleanup() { document.removeEventListener("mousedown", outside, true); pop.remove(); } };
  draw(); q.focus();
}

/** Mount every crsPickerField() inside `root` (idempotent; call after each render). */
export function enhanceCrsPickers(root, app) {
  if (!root || !app?.services?.projections) return;
  const svc = app.services.projections;
  for (const field of root.querySelectorAll("[data-crs-picker]")) {
    if (field.dataset.crsMounted) continue;
    field.dataset.crsMounted = "1";
    const hidden = field.querySelector("input[type=hidden]"), btn = field.querySelector(".crs-picker-btn");
    const extra = JSON.parse(field.dataset.extra || "[]");
    const label = () => {
      const sp = extra.find((x) => x.value === hidden.value);
      if (sp) { btn.textContent = sp.label; return; }
      const e = hidden.value ? svc.getCRS(hidden.value) : null;
      btn.textContent = e ? `${e.name} · ${e.code}` : hidden.value || field.dataset.placeholder || "Choose CRS…";
      if (e) btn.title = `${e.code}${e.unitsLabel ? ` · ${e.unitsLabel}` : ""}${e.datum ? ` · ${String(e.datum).replace(/_/g, " ")}` : ""}`;
    };
    label();
    if (hidden.value && !svc.getCRS(hidden.value)) svc.ensureCRS(hidden.value).then(label).catch(() => {});
    btn.addEventListener("click", (ev) => { ev.preventDefault(); ev.stopPropagation(); openPicker(field, app); });
  }
}

/**
 * Called once at startup: mounts any crsPickerField() as soon as it appears anywhere in the page, so
 * tools that rebuild their HTML from many places don't each need an enhanceCrsPickers() call.
 */
export function installCrsPickers(app) {
  if (typeof MutationObserver === "undefined" || installCrsPickers._done) return;
  installCrsPickers._done = true;
  let queued = false;
  const run = () => { queued = false; enhanceCrsPickers(document.body, app); };
  new MutationObserver((muts) => {
    if (queued) return;
    for (const m of muts) for (const n of m.addedNodes) {
      if (n.nodeType === 1 && (n.matches?.("[data-crs-picker]") || n.querySelector?.("[data-crs-picker]"))) { queued = true; queueMicrotask(run); return; }
    }
  }).observe(document.body, { childList: true, subtree: true });
  run();
}
